import Cookies from 'js-cookie'
import { Sdk } from '../Sdk'

export const getAvailableLanguages = () =>
  !Sdk.getConfig().env.isProduction
    ? Sdk.getConfig().variables.lang.dev
    : Sdk.getConfig().variables.lang.prod
// generally, do not comment languages out, configure in index.tsx the available ones
export const ALL_LANGUAGES = [
  {
    code: 'it',
    label: 'Italiano',
  },
  {
    code: 'en',
    label: 'English',
  },
  {
    code: 'es',
    label: 'Castellano',
  },
  {
    code: 'ca',
    label: 'Català',
  },
  {
    code: 'fr',
    label: 'Français',
  },
  {
    code: 'de',
    label: 'Deutsch',
  },
  {
    code: 'nl',
    label: 'Nederlands',
  },
  {
    code: 'pt',
    label: 'Português',
  },
]

/**
 * Languages enabled for the current app (Sdk.init variables.lang), resolved at
 * call time. Prefer this over LANGUAGES: that constant is filtered when this
 * module is first imported, which usually happens before Sdk.init runs, so it
 * only ever reflects the SDK defaults.
 */
export const getLanguages = () =>
  ALL_LANGUAGES.filter((lang) => getAvailableLanguages().includes(lang.code))

/** @deprecated use getLanguages(): this list is filtered against the SDK defaults at import time */
export const LANGUAGES = getLanguages()

export const DEFAULT_LANGUAGE = 'en'

export const isLocalhost = () =>
  Boolean(
    window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
    ),
  )

const isIpv4 = () =>
  window.location.hostname.match(
    /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(:[0-9]+)?$/,
  )

const isDevEnvironment = () =>
  window.location.hostname.match(/www-.*\.evenfi\.dev/)

// DISABLED REDIRECT ON CHANGE LANGUAGE
// const excludeLangRedirect = () =>
//   isLocalhost() || isIpv4() || isDevEnvironment()
const excludeLangRedirect = () => true

export const setCurrentLang = (lang, refUrl?) => {
  let currentUrl = window.location.href
  if (refUrl) {
    currentUrl = refUrl
  }
  // Avoid setting a non-existing language
  lang = getAvailableLanguages().indexOf(lang) > -1 ? lang : DEFAULT_LANGUAGE
  const prevLang = getCookieLang()
  setCookieLang(lang)
  const currentUrlLang = getURLLang() || DEFAULT_LANGUAGE

  if (!excludeLangRedirect() && currentUrlLang !== lang) {
    return getLangFullUrl(lang, currentUrl)
  }
  if (excludeLangRedirect() && prevLang !== lang) {
    return window.location.href
  }

  return null
}

export const setCurrentLangAndReload = (lang) => {
  const newUrl = setCurrentLang(lang)
  if (newUrl) {
    if (newUrl === window.location.href) {
      window.location.reload()
    } else {
      window.location.replace(newUrl)
    }
  }
}

const LANG_STORAGE_KEY = 'lang'

/** Registrable parent of the current host ("evenfi.com" for pynn.evenfi.com) */
const getParentDomain = () => {
  const labels = window.location.hostname.split('.')
  return labels.length > 2 ? labels.slice(-2).join('.') : null
}

const readStoredLang = () => {
  try {
    const value = localStorage.getItem(LANG_STORAGE_KEY)
    return getAvailableLanguages().includes(value) ? value : undefined
  } catch {
    return undefined
  }
}

const writeStoredLang = (lang: string) => {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang)
  } catch {
    /* Storage may be blocked. */
  }
}

/**
 * Every `lang` cookie the browser sends, in its own order. Legacy language
 * hosts (it.<brand>, ...) and older builds set the cookie on the parent
 * domain, and a user may still carry that one next to the app host's own:
 * browsers then send both and Chrome lists the older one first, so
 * `Cookies.get` would return the stale value and undo a language change.
 */
const readLangCookies = (): string[] => {
  const raw =
    typeof document !== 'undefined' && typeof document.cookie === 'string'
      ? document.cookie
      : ''
  const values = raw
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.startsWith('lang='))
    .map((part) => decodeURIComponent(part.slice('lang='.length)))
  if (values.length === 0) {
    const single = Cookies.get('lang')
    return single ? [single] : []
  }
  return values
}

const getCookieLang = () => {
  const available = getAvailableLanguages()
  const cookies = readLangCookies().filter((v) => available.includes(v))
  const stored = readStoredLang()
  // The app's own choice wins over a shadowing parent-domain cookie
  if (stored && (cookies.length !== 1 || cookies[0] === stored)) return stored
  return cookies[0]
}

export const setCookieLang = (lang) => {
  const domain = getBaseHostname()
  // Drop duplicates that would shadow the new value: a host-only cookie and
  // the parent-domain cookie left by legacy language hosts or older builds.
  Cookies.remove('lang')
  const parent = getParentDomain()
  if (parent && parent !== domain) Cookies.remove('lang', { domain: parent })
  writeStoredLang(lang)
  return Cookies.set('lang', lang, {
    domain,
    expires: 365, // keep the choice across browser sessions
  })
}

const getBrowserLang = () => {
  const getRootLang = (lang: string) =>
    lang?.includes('-') ? lang.split('-')[0] : lang
  const browserLang = (
    getRootLang(navigator.languages && navigator.languages[0]) ||
    getRootLang(navigator.language) ||
    //@ts-ignore
    getRootLang(navigator.userLanguage)
  )
    .toLowerCase()
    .substring(0, 2)

  return getAvailableLanguages().includes(browserLang) ? browserLang : undefined
}

export const getBestLang = () => {
  return getURLLang() || getCookieLang() || getBrowserLang() || DEFAULT_LANGUAGE
}

export const getBaseHostname = () => {
  const domain = window.location.hostname.split('.')
  while (getAvailableLanguages().indexOf(domain[0]) > -1) {
    domain.shift()
  }

  return domain.join('.')
}

export const getLangHostname = (lang) => {
  const domain = window.location.hostname.split('.')
  while (getAvailableLanguages().indexOf(domain[0]) > -1) {
    domain.shift()
  }

  if (lang !== DEFAULT_LANGUAGE) {
    domain.unshift(lang)
  }

  return domain.join('.')
}

export const getLangFullUrl = (lang, currentUrl = null) => {
  let refUrl = window.location.href
  if (currentUrl) {
    refUrl = currentUrl
  }
  const langHostname = getLangHostname(lang)
  return refUrl
    .replace('www.', '')
    .replace(window.location.hostname, langHostname)
}

export const getURLLang = () => {
  const languagesNoEn = getAvailableLanguages().filter((l) => l !== 'en')

  const lang = window.location.hostname.split('.')[0]
  if (languagesNoEn.indexOf(lang) > -1) {
    return lang
  }

  return null
}

// moment's plain "en" locale formats L as MM/DD/YYYY; every platform here is
// European, so English dates use the en-gb locale (DD/MM/YYYY). Keep in sync
// with DATE_FORMATS in @criptalia/common/time.
export const getMomentLocale = (lang?: string | null) =>
  lang === 'en' || !lang ? 'en-gb' : lang
